import type {
  CompositeImage,
  Itcr,
  Material,
  Operation,
  ProductMeta,
  QTables,
  Question,
  Resources,
  Routing,
  Tab,
  Validation,
} from "../types";
import { applyCustomAnswers } from "../util/customAnswers";
import { applyAnswerHideRules, applyAnswerReveals } from "../util/answerHideRules";
import { applyTableCellOptions } from "../util/tableCellOptions";
import { applyQuestionHideRules, applyQuestionLockRules } from "../util/questionHideRules";
import { markAbandonedOnUnload, recordLifecycle } from "../util/lifecycle";

// Computed lazily, NOT at module scope: import.meta.env exists only under Vite, so
// evaluating it on import made this module unimportable from Node — which broke the
// scripts/ suites the moment anything they load reached loader.ts. Nothing here
// should crash simply by being imported.
const dataBase = () => `${import.meta.env.BASE_URL}data`;

// Every /data/* URL carries the content hash of public/data (src/data/dataVersion.ts): the
// files are cached immutably and replaced in place on deploy, so the version travels in the
// URL. A code-only deploy leaves the data URLs unchanged and still cached.
import { versioned } from "./dataVersion";

// The fallback product for an unstamped snapshot lives with the snapshot format, in
// src/util/configSnapshot.ts (LEGACY_PRODUCT_SLUG) — deliberately not here, so a
// pure serialization module never has to import this one.

// One entry per configurator, from public/data/products.json.
export type ProductEntry = {
  // Folder under /data/ AND the value stamped into a saved snapshot. Never rename.
  slug: string;
  name: string;
  cpqId: string;
  // NetSuite transaction forms whose Configure opens this configurator directly (products.json).
  forms?: number[];
  enabled: boolean;
};

// Fetch a data file with a per-request timeout + a couple of retries. Without the
// timeout a single hung request (a slow/stalled CDN response — the cause of the
// stuck "Starting Flameco CPQ…" spinner) would block loadCpq forever, since
// fetch() has no default timeout. On a stall we abort and retry; if every attempt
// fails, we throw so App shows the error screen (with a Reload) instead of hanging.
// Per-file timing for the current load, collected by fetchJson and reported by loadCpq.
//
// Kevin, 2026-08-24, after a load died on composite_images.json: "there are certain
// scenarios where it wont give an error but we still need to track lifecycle time." So the
// retry count is kept too — three 20s timeouts and one slow 60s response produce a similar
// total but are completely different problems, and a total alone cannot tell them apart.
type FileTiming = { name: string; ms: number; attempts: number; failed?: boolean };
let fileTimings: FileTiming[] = [];
let fileRetries = 0;

async function fetchJson<T>(path: string, timeoutMs = 20000, attempts = 3): Promise<T> {
  const url = versioned(`${dataBase()}/${path}`);
  const started = typeof performance !== "undefined" ? performance.now() : Date.now();
  const since = () => (typeof performance !== "undefined" ? performance.now() : Date.now()) - started;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) });
      if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`);
      const body = (await res.json()) as T;
      fileTimings.push({ name: path, ms: since(), attempts: attempt });
      if (attempt > 1) fileRetries += attempt - 1;
      return body;
    } catch (e) {
      lastErr = e;
      // Back off briefly before retrying a timeout / network blip.
      if (attempt < attempts) await new Promise((r) => setTimeout(r, 500 * attempt));
    }
  }
  fileTimings.push({ name: path, ms: since(), attempts, failed: true });
  fileRetries += attempts - 1;
  throw lastErr instanceof Error
    ? new Error(`Could not load ${path} (network stalled after ${attempts} attempts): ${lastErr.message}`)
    : new Error(`Could not load ${path}`);
}

export type CpqData = {
  // Which configurator this data is. Carried on the data itself so anything holding
  // a CpqData knows what it is looking at without consulting module state.
  slug: string;
  product: ProductMeta;
  tabs: Tab[];
  questions: Question[];
  qtables: QTables;
  resources: Resources;
  operations: Operation[];
  materials: Material[];
  validations: Validation[];
  compositeImages: CompositeImage[];
  itcrs: Itcr[];
  // resource internal id -> local filename under /data/<slug>/resources/.
  resourceFiles: Record<string, string>;
};

/** A tool on the home page that is not a NetSuite CPQ configurator: its own page at `href`
 *  (the Laser Job, public/laser/), with no dataset, profile or snapshot. */
export type PageEntry = { slug: string; name: string; href: string; enabled: boolean };

type Registry = { products?: ProductEntry[]; pages?: PageEntry[] };
let registryP: Promise<Registry> | null = null;
/** products.json, fetched once however many callers ask. */
function loadRegistry(): Promise<Registry> {
  if (!registryP) registryP = fetchJson<Registry>("products.json").catch((e) => { registryP = null; throw e; });
  return registryP;
}

/** The home page's other tools. Never throws: a missing or malformed list is no tools. */
export async function loadPages(): Promise<PageEntry[]> {
  try {
    const body = await loadRegistry();
    return (Array.isArray(body.pages) ? body.pages : []).filter(
      (p) => p && p.enabled && typeof p.href === "string" && p.href.startsWith("/")
    );
  } catch {
    return [];
  }
}

/** The configurator registry. Small on purpose — it is fetched before any dataset,
 *  so the home page can be drawn without committing to a product. */
export async function loadProducts(): Promise<ProductEntry[]> {
  const body = await loadRegistry();
  const list = Array.isArray(body.products) ? body.products : [];
  // A registry with no usable entry is a deployment fault, not an empty state:
  // fail loudly rather than showing an empty picker that looks like "no products".
  if (list.length === 0) throw new Error("products.json lists no configurators");
  return list;
}

// The product whose dataset is currently loaded. Exactly one configurator is active
// per page load — you cannot configure two at once — so this is held here rather
// than threaded through every call site. It is what keeps loadRoutings() and
// resourceUrl() honest: both used to hardcode a single product's path, and a
// forgotten argument would have silently served Natural Draft data inside another
// configurator.
let activeSlug: string | null = null;

/** URL of a locally-cached resource image for the ACTIVE product. '' when there is
 *  no local copy (the caller falls back to NetSuite). */
export function resourceUrl(fileName: string): string {
  if (!fileName || !activeSlug) return "";
  // Versioned for the same reason the JSON is: these live under /data/ and are covered by the
  // same immutable cache rule.
  return versioned(`${dataBase()}/${activeSlug}/resources/${encodeURIComponent(fileName)}`);
}

// routings.json is large (~7.6 MB), so it is NOT part of the initial load: fetched
// on demand (e.g. when previewing/creating the NetSuite assembly). Cached PER SLUG
// — a single shared cache would hand one configurator the other's routing rules,
// which is exactly the class of bug this restructure has to avoid.
const routingsCache: Record<string, Promise<Routing[]>> = {};
export function loadRoutings(): Promise<Routing[]> {
  if (!activeSlug) {
    return Promise.reject(new Error("loadRoutings called before a product was loaded"));
  }
  const slug = activeSlug;
  // Longer per-attempt timeout than the small config files get, to avoid a false
  // timeout on a slow connection.
  if (!routingsCache[slug]) {
    // Timed separately from app-load: it is ~7.6 MB fetched on demand during Save & Submit,
    // so when a submit feels slow this is the first thing worth ruling in or out. Cached, so
    // only the first call per product is measured -- which is the one that costs anything.
    const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
    const since = () => (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
    routingsCache[slug] = fetchJson<Routing[]>(`${slug}/routings.json`, 60000).then(
      (r) => {
        recordLifecycle({ lifecycle: "routings", outcome: "ok", totalMs: since(), product: slug,
          context: { rules: Array.isArray(r) ? r.length : null } });
        return r;
      },
      (e) => {
        const msg = e instanceof Error ? e.message : String(e);
        recordLifecycle({ lifecycle: "routings", product: slug, totalMs: since(),
          outcome: /stalled|timed out|timeout|abort/i.test(msg) ? "timeout" : "error", reason: msg });
        // A failed fetch must not stay in the cache, or every later submit in this session
        // reuses the rejection and the user can never recover without a reload.
        delete routingsCache[slug];
        throw e;
      }
    );
  }
  return routingsCache[slug];
}

/** Load one configurator's dataset and make it the active product.
 *
 *  Timed end to end into cpq2_timing, however it ends. The files are fetched in parallel, so
 *  the total is the SLOWEST file rather than their sum — which is exactly why the per-file
 *  breakdown is kept: "app-load took 61s" is not actionable, "composite_images.json took 61s
 *  across 3 attempts while everything else took under 400ms" is. */
export async function loadCpq(slug: string): Promise<CpqData> {
  if (!slug) throw new Error("loadCpq requires a product slug");
  fileTimings = [];
  fileRetries = 0;
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const elapsed = () => (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
  // If the tab is closed while this is still running, that run is reported as abandoned
  // rather than silently disappearing — the stuck spinner is the case most likely to be
  // given up on, so it is the one that must not be missing from the numbers.
  const settled = markAbandonedOnUnload("app-load", { product: slug });
  try {
    const data = await loadCpqInner(slug);
    settled();
    recordLifecycle({
      lifecycle: "app-load", outcome: "ok", totalMs: elapsed(), product: slug,
      steps: fileTimings.map((f) => ({ name: f.name, ms: f.ms })),
      retries: fileRetries,
      reason: fileRetries > 0 ? `succeeded after ${fileRetries} retry(ies)` : null,
      context: { files: fileTimings.length },
    });
    return data;
  } catch (e) {
    settled();
    const msg = e instanceof Error ? e.message : String(e);
    const failed = fileTimings.find((f) => f.failed);
    recordLifecycle({
      lifecycle: "app-load",
      // "network stalled" / "signal timed out" is a give-up, not a fault in the data.
      outcome: /stalled|timed out|timeout|abort/i.test(msg) ? "timeout" : "error",
      totalMs: elapsed(), product: slug, reason: msg,
      steps: fileTimings.map((f) => ({ name: f.name, ms: f.ms, failed: f.failed })),
      retries: fileRetries,
      context: { failedFile: failed ? failed.name : null, filesCompleted: fileTimings.filter((f) => !f.failed).length },
    });
    throw e;
  }
}

async function loadCpqInner(slug: string): Promise<CpqData> {
  const p = (file: string) => `${slug}/${file}`;
  const [
    product,
    tabs,
    questions,
    qtables,
    resources,
    operations,
    materials,
    validations,
    compositeImages,
    itcrs,
    resourceFiles,
  ] = await Promise.all([
    fetchJson<ProductMeta>(p("product_meta.json")),
    fetchJson<Tab[]>(p("tabs.json")),
    fetchJson<Question[]>(p("questions.json")),
    fetchJson<QTables>(p("qtable_data.json")),
    fetchJson<Resources>(p("resources.json")),
    fetchJson<Operation[]>(p("operations.json")),
    fetchJson<Material[]>(p("materials.json")),
    fetchJson<Validation[]>(p("validations.json")),
    fetchJson<CompositeImage[]>(p("composite_images.json")),
    fetchJson<Itcr[]>(p("itcrs.json")),
    // Local copies of the resource images; optional (falls back to NetSuite).
    fetchJson<Record<string, string>>(p("resources/manifest.json")).catch(() => ({})),
  ]);
  // Set only after every fetch has SUCCEEDED. Setting it up front would leave a
  // failed load with a half-active product, so a retry of a different configurator
  // could resolve routings/resources against the one that failed.
  activeSlug = slug;
  await prepareQuestions(slug, questions);
  return {
    slug,
    product,
    tabs,
    questions,
    qtables,
    resources,
    operations,
    materials,
    validations,
    compositeImages,
    itcrs,
    resourceFiles,
  };
}

/** One configurator's questions and nothing else, prepared exactly as loadCpq prepares
 *  them. For line-extras.html, which needs answer labels to rebuild a saved configuration's
 *  Serial Tag and additional lines and has no use for the other ten files. Does not make the
 *  product active and records no app-load. */
export async function loadQuestions(slug: string): Promise<Question[]> {
  if (!slug) throw new Error("loadQuestions requires a product slug");
  const questions = await fetchJson<Question[]>(`${slug}/questions.json`);
  await prepareQuestions(slug, questions);
  return questions;
}

/** Everything the configurator does to questions.json after fetching it, in order. */
export async function prepareQuestions(slug: string, questions: Question[]): Promise<void> {
  // Option-level hide rules this configurator owns (Flame Cells narrows Core ID/OD by core
  // type, and Face Plate Size by core ID/OD). Merged into the answers' own parsed hide rule,
  // so from here on nothing can tell them apart from rules the export shipped.
  //
  // BEFORE applyCustomAnswers, which CLONES an existing answer to build a runtime option: a
  // clone made after this ran would inherit whatever rule happened to be on its template.
  // (mergeCustomAnswers clears *_rule fields on the clone, so this is belt and braces -- but
  // the ordering is free and the failure it prevents is an option that silently cannot be
  // picked.)
  // First the other direction: an option the export hides outright that this configurator
  // offers anyway (NDB's HD ignitor type). Before the hide rules, so a rule of ours can still
  // narrow a revealed option.
  applyAnswerReveals(slug, questions);
  applyAnswerHideRules(slug, questions);
  // And the same idea one level down: a TABLE column's cell options, which answerHideRules
  // cannot reach because its answers are the columns themselves. NDB's Inlet Connection Size
  // offers twenty-one fractions where eight couplings exist.
  applyTableCellOptions(slug, questions);
  // And whole questions, for a condition the export does not carry -- NDB hides Hood Spacers
  // and the Face Plate on an actuator design. Paired with an operation that forces the value,
  // because a hidden question keeps its answer.
  applyQuestionHideRules(slug, questions);
  // And questions shown but locked to the one answer a rule allows (NDB's Hood Sight Tube on
  // an actuator design).
  applyQuestionLockRules(slug, questions);

  // Options added at runtime by another session (a Flange to Nozzle End size someone typed)
  // live in the database, not in questions.json, which is static CDN content. Merging them
  // in here also hands their tokens to the MAPPING encoder - without that, selecting one
  // would encode the slot's null token and the value would vanish from the item.
  //
  // Deliberately AFTER the Promise.all and never fatal: a configurator whose extra options
  // cannot be fetched must still open. applyCustomAnswers swallows its own errors, and this
  // guard covers anything it cannot.
  try {
    await applyCustomAnswers(slug, questions);
  } catch (e) {
    console.warn("[cpq2] runtime answer options unavailable — continuing without them", e);
  }
}
